import { supabase } from './authService/supabase';
import { resolveApiRequestUrl } from './requestRouting';
import { reportApiTiming } from './rumReporter';

export interface StreamApiConfig {
    baseURL: string;
    headers?: Record<string, string>;
}

export interface StreamPostOptions {
    url: string;
    body?: any;
    headers?: Record<string, string>;
    onMessage: (data: any) => void;
    onError?: (error: any) => void;
    signal?: AbortSignal;
}

export interface StreamGetOptions {
    url: string;
    params?: any;
    headers?: Record<string, string>;
    onMessage: (data: any) => void;
    onError?: (error: any) => void;
    signal?: AbortSignal;
}

type Middleware = (request: Request, next: () => Promise<Response>) => Promise<Response>;

type StreamTimingStats = {
    firstChunkMs: number | null;
    messageCount: number;
    aborted: boolean;
};

export class StreamApi {
    private baseURL: string;
    private headers: Record<string, string>;
    private middlewares: Middleware[] = [];

    constructor(config: StreamApiConfig) {
        this.baseURL = config.baseURL;
        this.headers = {
            'Content-Type': 'application/json',
            ...(config.headers || {})
        };

        // 添加认证中间件
        this.use(async (request, next) => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.access_token) {
                request.headers.set('Authorization', `Bearer ${session.access_token}`);
            }

            if (typeof window !== 'undefined' && (window as any).Bustly?.workspace_id) {
                request.headers.set('X-Workspace-Id', (window as any).Bustly.workspace_id);
            }

            return next();
        });
    }

    /**
     * 添加中间件
     * @param middleware 中间件函数
     */
    public use(middleware: Middleware): void {
        this.middlewares.push(middleware);
    }



    /**
     * 执行请求
     * @param url 请求URL
     * @param options 请求选项
     */
    private async executeRequest(url: string, options: RequestInit): Promise<Response> {
        const request = new Request(resolveApiRequestUrl(url, { baseURL: this.baseURL }), options);

        const executeMiddleware = async (index: number): Promise<Response> => {
            if (index >= this.middlewares.length) {
                return fetch(request);
            }

            const middleware = this.middlewares[index];
            return middleware(request, () => executeMiddleware(index + 1));
        };

        return executeMiddleware(0);
    }

    /**
     * 发起流式 POST 请求
     * @param options StreamPostOptions
     */
    public async post(options: StreamPostOptions): Promise<void> {
        const {
            url, body, headers, onMessage, onError, signal 
        } = options;
        const startedAt = Date.now();
        let timingReported = false;
        
        try {
            const requestHeaders = {
                ...this.headers,
                ...(headers || {})
            };

            if (body instanceof FormData) {
                delete requestHeaders['Content-Type'];
            }

            const response = await this.executeRequest(url, {
                method: 'POST',
                headers: requestHeaders,
                body: body instanceof FormData ? body : JSON.stringify(body),
                signal
            });

            if (!response.ok || !response.body) {
                this.reportStreamTiming({
                    method: 'POST',
                    route: url,
                    status: response.status,
                    startedAt,
                    ok: false,
                    errorType: response.ok ? 'exception' : 'http',
                });
                timingReported = true;
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const stats = await this.handleStreamResponse(response, onMessage, signal, startedAt);
            this.reportStreamTiming({
                method: 'POST',
                route: url,
                status: response.status,
                startedAt,
                ok: !stats.aborted,
                stats,
                errorType: stats.aborted ? 'abort' : undefined,
            });
        } catch (error) {
            if (signal?.aborted) {
                this.reportStreamTiming({
                    method: 'POST',
                    route: url,
                    status: null,
                    startedAt,
                    ok: false,
                    errorType: 'abort',
                    aborted: true,
                });
                // 请求被中止，不调用 onError
                return;
            }
            if (!timingReported) {
                this.reportStreamTiming({
                    method: 'POST',
                    route: url,
                    status: null,
                    startedAt,
                    ok: false,
                    errorType: 'exception',
                });
            }
            if (onError) onError(error);
            else throw error;
        }
    }

    /**
     * 发起流式 GET 请求
     * @param options StreamGetOptions
     */
    public async get(options: StreamGetOptions): Promise<void> {
        const {
            url, params, headers, onMessage, onError, signal 
        } = options;
        const startedAt = Date.now();
        let timingReported = false;
        
        try {
            const requestHeaders = {
                ...this.headers,
                ...(headers || {})
            };

            let fullUrl = url;
            if (params) {
                const searchParams = new URLSearchParams(params);
                fullUrl += `?${searchParams.toString()}`;
            }

            const response = await this.executeRequest(fullUrl, {
                method: 'GET',
                headers: requestHeaders,
                signal
            });

            if (!response.ok || !response.body) {
                this.reportStreamTiming({
                    method: 'GET',
                    route: fullUrl,
                    status: response.status,
                    startedAt,
                    ok: false,
                    errorType: response.ok ? 'exception' : 'http',
                });
                timingReported = true;
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const stats = await this.handleStreamResponse(response, onMessage, signal, startedAt);
            this.reportStreamTiming({
                method: 'GET',
                route: fullUrl,
                status: response.status,
                startedAt,
                ok: !stats.aborted,
                stats,
                errorType: stats.aborted ? 'abort' : undefined,
            });
        } catch (error) {
            if (signal?.aborted) {
                this.reportStreamTiming({
                    method: 'GET',
                    route: url,
                    status: null,
                    startedAt,
                    ok: false,
                    errorType: 'abort',
                    aborted: true,
                });
                // 请求被中止，不调用 onError
                return;
            }
            if (!timingReported) {
                this.reportStreamTiming({
                    method: 'GET',
                    route: url,
                    status: null,
                    startedAt,
                    ok: false,
                    errorType: 'exception',
                });
            }
            if (onError) onError(error);
            else throw error;
        }
    }

    private reportStreamTiming(params: {
        method: string;
        route: string;
        status: number | null;
        startedAt: number;
        ok: boolean;
        stats?: StreamTimingStats;
        aborted?: boolean;
        errorType?: 'http' | 'exception' | 'abort';
    }): void {
        reportApiTiming({
            method: params.method,
            route: params.route,
            status: params.status,
            durationMs: Math.max(0, Date.now() - params.startedAt),
            timeout: false,
            ok: params.ok,
            kind: 'stream',
            firstChunkMs: params.stats?.firstChunkMs ?? null,
            messageCount: params.stats?.messageCount ?? 0,
            aborted: params.aborted ?? params.stats?.aborted ?? false,
            errorType: params.errorType,
        });
    }

    /**
     * 处理流式响应
     * @param response Response对象
     * @param onMessage 消息回调
     * @param signal AbortSignal
     */
    private async handleStreamResponse(
        response: Response, 
        onMessage: (data: any) => void, 
        signal: AbortSignal | undefined,
        startedAt: number
    ): Promise<StreamTimingStats> {
        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let done = false;
        let buffer = '';
        let currentEvent = '';
        let currentData = '';
        let firstChunkMs: number | null = null;
        let messageCount = 0;

        const markMessage = () => {
            if (firstChunkMs === null) {
                firstChunkMs = Math.max(0, Date.now() - startedAt);
            }
            messageCount += 1;
        };

        while (!done) {
            // 检查是否被中止
            if (signal?.aborted) {
                await reader.cancel();
                return { firstChunkMs, messageCount, aborted: true };
            }

            const { value, done: readerDone } = await reader.read();
            if (value) {
                buffer += decoder.decode(value, { stream: !readerDone });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';
                
                for (const line of lines) {
                    if (line.trim() === '') {
                        // 空行表示一个完整的SSE消息结束
                        if (currentEvent && currentData) {
                            try {
                                const data = JSON.parse(currentData);
                                data.event_type = currentEvent;
                                markMessage();
                                onMessage(data);
                            } catch (e) {
                                // 忽略解析失败的数据
                                console.warn('Failed to parse SSE data:', currentData, e);
                            }
                        }
                        currentEvent = '';
                        currentData = '';
                    } else if (line.startsWith('event: ')) {
                        currentEvent = line.slice(7).trim();
                    } else if (line.startsWith('data: ')) {
                        currentData = line.slice(6).trim();
                    }
                }
            }
            
            // 更新循环条件
            if (readerDone) {
                done = true;
            }
        }

        // 处理最后剩余的数据
        if (currentEvent && currentData) {
            try {
                const data = JSON.parse(currentData);
                data.event_type = currentEvent;
                markMessage();
                onMessage(data);
            } catch (e) {
                console.warn('Failed to parse final SSE data:', currentData, e);
            }
        }

        return { firstChunkMs, messageCount, aborted: false };
    }
}
